import superagent from 'superagent';
import { commonStore, userStore } from '../stores';

const API_ROOT = process.env.REACT_APP_API_HOST_NAME;
const CHATBOT_MANAGEMENT_ROOT =
  process.env.REACT_APP_CHATBOT_MANAGEMENT_HOST_NAME;

const responseBody = (res) => {
  if (res.type === 'text/csv') {
    return res.text;
  }
  return res.body;
};

const handleError = (err) => {
  return err;
};

/**
 * Read the value of a cookie by name.
 */
function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${  name  }=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Build a superagent plugin that attaches the X-CSRFToken header on every
 * state-changing request (POST, PUT, PATCH, DELETE).
 *
 * Each Django service uses a unique CSRF_COOKIE_NAME to avoid collisions
 * on the shared .joboti.com domain:
 *   srv.router     → router_csrftoken
 *   srv.chatserver → chatserver_csrftoken
 */
const makeCsrfPlugin = (cookieName) => (req) => {
  const safeMethods = ['GET', 'HEAD', 'OPTIONS'];
  if (!safeMethods.includes(req.method)) {
    const token = getCookie(cookieName);
    if (token) {
      req.set('X-CSRFToken', token);
    }
  }
  return req;
};

const csrfPlugin = makeCsrfPlugin('router_csrftoken');
const chatserverCsrfPlugin = makeCsrfPlugin('chatserver_csrftoken');

const tokenPlugin = (token) => (req) =>
  req.set('Authorization', `Token ${token}`);

const jwtPlugin =
  (authorization = null) =>
    (req) => {
      if (authorization) {
        return req.set('Authorization', authorization);
      }

      return req;
    };

const requests = {
  del: (url, body) =>
    superagent
      .del(`${API_ROOT}${url}`, body)
      .withCredentials()
      .use(csrfPlugin)
      .then(responseBody)
      .catch(handleError),
  get: (url, params = {}) =>
    superagent
      .get(`${API_ROOT}${url}`, params)
      .withCredentials()
      .then(responseBody)
      .catch(handleError),
  manualGet: (url, params = {}) =>
    superagent
      .get(`${url}`, params)
      .withCredentials()
      .then(responseBody)
      .catch(handleError),
  put: (url, body) =>
    superagent
      .put(`${API_ROOT}${url}`, body)
      .withCredentials()
      .use(csrfPlugin)
      .then(responseBody)
      .catch(handleError),
  post: (url, body, authorization = null) =>
    superagent
      .post(`${API_ROOT}${url}`, body)
      .withCredentials()
      .use(csrfPlugin)
      .use(jwtPlugin(authorization))
      .then(responseBody)
      .catch(handleError),
  plainPost: (url, body) =>
    superagent
      .post(`${API_ROOT}${url}`, body)
      .withCredentials()
      .use(csrfPlugin)
      .then(responseBody)
      .catch(handleError),
  manualPost: (url, body) =>
    superagent
      .post(url, body)
      .withCredentials()
      .use(csrfPlugin)
      .then(responseBody)
      .catch(handleError),
  gql: {
    router: {
      query: (query, variables) =>
        superagent
          .get(`${API_ROOT}/graphql`)
          .withCredentials()
          .query({ query })
          .query({ variables: JSON.stringify(variables) })
          .then(responseBody)
          .catch(handleError),
      mutate: (query, variables) =>
        superagent
          .post(`${API_ROOT}/graphql`)
          .withCredentials()
          .use(csrfPlugin)
          .query({ query })
          .send({
            query,
            variables: JSON.stringify(variables),
          })
          .then(responseBody)
          .catch(handleError),
    },
    chatserver: {
      query: (
        query,
        variables,
        graphQlServer = `https://${commonStore.selectedChatbot.serverAddress}/graphql`
      ) =>
        superagent
          .post(`${graphQlServer}`)
          .withCredentials()
          .use(chatserverCsrfPlugin)
          .send({
            query,
            variables: JSON.stringify(variables),
          })
          .then(responseBody)
          .catch(handleError),
      mutate: (
        query,
        variables,
        graphQlServer = `https://${commonStore.selectedChatbot.serverAddress}/graphql`
      ) =>
        superagent
          .post(`${graphQlServer}`)
          .withCredentials()
          .use(chatserverCsrfPlugin)
          .send({
            query,
            variables: JSON.stringify(variables),
          })
          .then(responseBody)
          .catch(handleError),
    },
  },
  chatbotManagement: {
    post: (url, body, token = '') =>
      superagent
        .post(`${CHATBOT_MANAGEMENT_ROOT}${url}`, body)
        .use(tokenPlugin(token))
        .then(responseBody)
        .catch(handleError),
  },
};

export { getCookie };
export default requests;
